import React from "react";
import { apiLembarKerja, apiUnduhLembarKerja, lembarKerjaMockupUrl, formatApiErrorDetail } from "@/lib/api";
import { rencanaLk, gabung, kerahLk, tanggalLk, bolehUnduhLk, skalaA4, LEBAR_A4, barisUkuran, slotLain, labelUtama } from "@/lib/lembarKerja";
import { Download, Loader2, ZoomIn, ZoomOut } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { usePengaturan } from "@/hooks/usePengaturan";

// owner 2026-10-12: a Jersey SPK as a worksheet (menu SPK) + "Unduh Excel (A4)".
// Owner 2026-10-15 (TAMBAHAN-OWNER-OKT A7): on screen it looks like the Excel A4 sheet
// (same blocks, same order); the Excel download only for Owner, Manager and Finance —
// every other role views it here (the server refuses the download as well).
// Only data the order already holds; no prices. Players' names per role (server).
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;

export async function unduhLembarKerja(spkId) {
  const { blob, nama } = await apiUnduhLembarKerja(spkId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = nama;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// v2.18 B: `ikon` = a small download icon for a row of the SPK list (desktop table and phone card)
export function TombolUnduhLembarKerja({ spkId, testid = "lk-unduh", kecil = false, terang = false, ikon = false }) {
  const { user } = useAuth();
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState(null);
  if (!bolehUnduhLk(user?.role)) return null;
  const klik = async () => {
    setBusy(true); setError(null);
    try { await unduhLembarKerja(spkId); } catch (e) { setError(errText(e)); } finally { setBusy(false); }
  };
  if (ikon) return (
    <span className="inline-flex flex-col items-end gap-1">
      <button type="button" data-testid={testid} disabled={busy} onClick={(e) => { e.stopPropagation(); klik(); }} title="Unduh Excel lembar kerja (A4)" aria-label="Unduh Excel lembar kerja"
        className="inline-flex items-center rounded-md border border-slate-700 bg-slate-800/60 px-2.5 py-1.5 text-xs text-slate-300 hover:border-emerald-500/40 hover:text-emerald-300 disabled:opacity-50">
        {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
      </button>
      {error && <span data-testid={`${testid}-error`} className="max-w-[14rem] text-right text-[11px] text-red-300">{error}</span>}
    </span>
  );
  return (
    <span className="inline-flex flex-col gap-1">
      <button data-testid={testid} disabled={busy} onClick={klik}
        className={`inline-flex items-center justify-center gap-2 rounded-lg font-semibold disabled:opacity-50 ${terang ? "border-2 border-soq-biru bg-white text-soq-biru hover:bg-slate-100" : "bg-amber-500 text-slate-950 hover:bg-amber-400"} ${kecil ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm"}`}>
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Download size={15} />} Unduh Excel (A4)
      </button>
      {error && <span data-testid={`${testid}-error`} className="text-xs text-red-300">{error}</span>}
    </span>
  );
}

// ---- the A4 sheet: Excel look — thin grey grid, pale-blue section titles, royal-blue title bar
const GARIS = "border border-[#808080]";
const PUCAT = "bg-[#E8ECFB] text-[#1020D0]";
const Judul = ({ children, testid }) => (
  <div data-testid={testid} className={`${GARIS} ${PUCAT} py-[3px] text-center text-[12px] font-bold`}>{children}</div>
);
const LabelNilai = ({ label, children, testid, tebal }) => (
  <div className="grid grid-cols-[31%_69%] text-[11.5px] leading-tight">
    <div className={`${GARIS} border-t-0 px-1.5 py-[3px] text-[10.5px]`}>{label}</div>
    <div data-testid={testid} className={`${GARIS} border-l-0 border-t-0 min-w-0 break-words px-1.5 py-[3px] ${tebal ? "font-bold" : ""}`}>{children || "-"}</div>
  </div>
);
const Sel = ({ children, className = "", ...rest }) => <td className={`${GARIS} px-1 py-[3px] ${className}`} {...rest}>{children}</td>;

function Lembar({ d, spkId, namaUsaha }) {
  const [gagal, setGagal] = React.useState({});
  const lain = slotLain(d.mockup);
  const utama = labelUtama(d.mockup);
  const ket = !d.mockup ? "Mockup belum ada" : d.mockup.acc ? "Mockup versi ACC customer" : "Mockup terbaru (belum ACC)";
  return (
    <div data-testid="lk-lembar" className="bg-white p-[28px] text-[#111] shadow-lg ring-1 ring-black/10" style={{ width: LEBAR_A4 }}>
      {/* title: royal blue, white SOQ logo, thin orange line */}
      <div data-testid="lk-judul" className="relative flex h-[36px] items-center justify-center border-b-[3px] border-[#FC9400] bg-[#1020D0] text-[15px] font-bold text-white">
        <Logo varian="putih" className="absolute left-2 h-[24px] w-auto" />
        <span className="truncate px-[120px]">LEMBAR KERJA JERSEY — {d.tim || d.customer}</span>
      </div>
      <div className="py-[3px] text-center text-[9px] text-[#333]">{[namaUsaha, "Tanpa harga: rincian uang hanya di nota", "Dicetak dari SOQPRINT"].filter(Boolean).join(" · ")}</div>

      <div className="grid grid-cols-[47%_51%] justify-between">
        {/* left: strip, mockup + labelled pictures, data, specification */}
        <div className="min-w-0">
          <Judul>{d.nomor_spk} · {d.nomor_faktur}</Judul>
          <div data-testid="lk-mockup">
            {d.mockup && !gagal.utama ? (
              <a href={lembarKerjaMockupUrl(spkId)} target="_blank" rel="noreferrer" className="block p-1">
                <img src={lembarKerjaMockupUrl(spkId)} alt={`Mockup ${d.nomor_spk}`} onError={() => setGagal({ ...gagal, utama: true })} className="mx-auto max-h-[420px] w-full object-contain" />
              </a>
            ) : <div className="py-8 text-center text-[11px] text-[#666]">Mockup belum ada</div>}
            <div className={`${GARIS} py-[2px] text-center text-[9.5px]`}>{utama ? `${utama} · ${ket}` : ket}</div>
            {lain.length > 0 && (
              <div data-testid="lk-slot" className="grid grid-cols-3">
                {lain.map((s) => (
                  <figure key={s.versi} data-testid={`lk-slot-${s.slot}`} className={`${GARIS} border-t-0 m-0`}>
                    <figcaption className={`${GARIS} border-x-0 border-t-0 py-[1px] text-center text-[9.5px]`}>{s.label}</figcaption>
                    <a href={lembarKerjaMockupUrl(spkId, s.versi)} target="_blank" rel="noreferrer" className="block h-[84px] p-1">
                      {!gagal[s.versi] && <img src={lembarKerjaMockupUrl(spkId, s.versi)} alt={s.label} onError={() => setGagal({ ...gagal, [s.versi]: true })} className="mx-auto h-full w-full object-contain" />}
                    </a>
                  </figure>
                ))}
              </div>
            )}
          </div>

          <div className="mt-0"><Judul>Data Pesanan</Judul></div>
          <div data-testid="lk-info">
            <LabelNilai label="No. SPK" testid="lk-spk" tebal>{d.nomor_spk}</LabelNilai>
            <LabelNilai label="Faktur" testid="lk-faktur">{d.nomor_faktur}</LabelNilai>
            <LabelNilai label="Tim / komunitas" testid="lk-tim">{d.tim}</LabelNilai>
            <LabelNilai label="Customer" testid="lk-customer">{d.customer}</LabelNilai>
            <LabelNilai label="Tanggal order">{tanggalLk(d.tanggal_order)}</LabelNilai>
            <LabelNilai label="Rencana selesai" testid="lk-rencana">{rencanaLk(d)}</LabelNilai>
            <LabelNilai label="Ambil / kirim">{`${tanggalLk(d.ambil_tanggal)} · ${d.ambil_cara || "-"}`}</LabelNilai>
            <LabelNilai label="Desainer">{d.desainer}</LabelNilai>
            <LabelNilai label="Kasir/CS" testid="lk-cs">{d.dibuat_oleh}</LabelNilai>
            <LabelNilai label="Total" testid="lk-total" tebal>{`${d.total_pcs} pcs${d.revisi_ke ? ` · revisi ${d.revisi_ke}` : ""}`}</LabelNilai>
            <LabelNilai label="Posisi" testid="lk-posisi">{d.posisi}</LabelNilai>
          </div>

          <Judul>Spesifikasi Pesanan</Judul>
          {d.variasi.map((v) => (
            <div key={v.no} data-testid={`lk-variasi-${v.no}`}>
              <div className={`${GARIS} border-t-0 px-1.5 py-[3px] text-[11.5px] font-bold text-[#1020D0]`}>Variasi {v.no} — {v.qty} pcs</div>
              <LabelNilai label="Barang">{v.barang}</LabelNilai>
              <LabelNilai label="Jenis jersey">{v.jenis_jersey}</LabelNilai>
              <LabelNilai label="Model" tebal>{v.model}</LabelNilai>
              <LabelNilai label="Kerah" tebal>{kerahLk(v) === "-" ? null : kerahLk(v)}</LabelNilai>
              <LabelNilai label="Bahan">{v.bahan}</LabelNilai>
              <LabelNilai label="Warna">{v.warna}</LabelNilai>
              {v.motif && <LabelNilai label="Motif">{v.motif}</LabelNilai>}
              {v.cutting && <LabelNilai label="Cutting">{v.cutting}</LabelNilai>}
              {v.tambahan_jahit.length > 0 && <LabelNilai label="Tambahan jahitan" testid={`lk-tambahan-jahit-${v.no}`}>{v.tambahan_jahit.join(", ")}</LabelNilai>}
              {v.catatan && <LabelNilai label="Catatan">{v.catatan}</LabelNilai>}
            </div>
          ))}
          {d.tambahan.length > 0 && (
            <div data-testid="lk-tambahan">
              <div className={`${GARIS} border-t-0 px-1.5 py-[3px] text-[11.5px] font-bold text-[#1020D0]`}>Tambahan</div>
              {d.tambahan.map((t, i) => <LabelNilai key={i} label={`${t.qty} ${t.satuan || ""}`}>{gabung(t.barang, t.catatan)}</LabelNilai>)}
            </div>
          )}
        </div>

        {/* right: size recap across (TOTAL last, orange frame), then the players */}
        <div className="min-w-0">
          <Judul testid="lk-ukuran">REKAP SIZE</Judul>
          <table className="w-full table-fixed border-collapse text-center">
            <tbody>
              {barisUkuran(d.ukuran, d.total_pcs).map((b, i) => (
                <React.Fragment key={i}>
                  <tr className="text-[10.5px] font-bold text-[#1020D0]">{b.kepala.map((k, j) => <Sel key={j} className={k === "TOTAL" ? "border-[#FC9400] border-2" : ""}>{k}</Sel>)}</tr>
                  <tr className="text-[15px] font-bold">{b.isi.map((v, j) => (
                    <Sel key={j} data-testid={b.kepala[j] === "TOTAL" ? "lk-total-size" : undefined}
                      className={b.kepala[j] === "TOTAL" ? "border-[#FC9400] border-2 text-[17px] text-[#0A1240]" : ""}>{v}</Sel>))}</tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
          {d.lengan.length > 0 && <div className={`${GARIS} border-t-0 py-[3px] text-center text-[11.5px]`}>Lengan: {d.lengan.map((x) => `${x.lengan || "-"} ${x.jumlah}`).join(" · ")}</div>}
          {/* v2.23: a split order may have no player list yet (names come after the customer's ACC) */}
          {d.lengan.length === 0 && d.peringatan_lengan && <div data-testid="lk-peringatan-lengan" className={`${GARIS} border-t-0 bg-[#FFF4D6] py-[4px] text-center text-[12px] font-bold text-[#B00000]`}>⚠ {d.peringatan_lengan}</div>}
          {d.ukuran_custom_catatan && <div className={`${GARIS} border-t-0 px-1.5 py-[3px] text-[11.5px]`}>Ukuran custom: {d.ukuran_custom_catatan}</div>}

          <div className="h-[14px]" />
          {d.pemain_disembunyikan ? (
            <>
              <Judul>REKAP PER VARIASI (tanpa nama &amp; nomor untuk pos ini)</Judul>
              <table data-testid="lk-rekap" className="w-full border-collapse text-[11.5px]">
                <thead><tr className="text-[10.5px] font-bold text-[#1020D0]"><Sel>Var</Sel><Sel>Ukuran</Sel><Sel>Lengan</Sel><Sel>Jumlah</Sel></tr></thead>
                <tbody>{d.rekap_pemain.map((x, i) => <tr key={i} className="text-center"><Sel>{x.variasi_no || "-"}</Sel><Sel>{x.ukuran}</Sel><Sel>{x.lengan}</Sel><Sel>{x.jumlah}</Sel></tr>)}</tbody>
              </table>
            </>
          ) : (
            <>
              <Judul>DAFTAR NAMA &amp; NOMOR ({d.pemain.length} pemain)</Judul>
              <table data-testid="lk-pemain" className="w-full table-fixed border-collapse text-[11.5px]">
                <colgroup><col style={{ width: "8%" }} /><col style={{ width: "12%" }} /><col style={{ width: "13%" }} /><col style={{ width: "40%" }} /><col style={{ width: "17%" }} /><col style={{ width: "10%" }} /></colgroup>
                <thead><tr className="text-center text-[10.5px] font-bold text-[#1020D0]"><Sel>No</Sel><Sel>Size</Sel><Sel>Nomor</Sel><Sel>Nama</Sel><Sel>Lengan</Sel><Sel>Var</Sel></tr></thead>
                <tbody>{d.pemain.map((p) => (
                  <tr key={p.no} data-testid={`lk-pemain-${p.no}`}>
                    <Sel className="text-center">{p.no}</Sel><Sel className="text-center">{p.ukuran}</Sel>
                    <Sel className="text-center text-[13px] font-bold">{p.nomor}</Sel>
                    <Sel className="truncate">{p.nama}{p.catatan ? ` (${p.catatan})` : ""}</Sel>
                    <Sel className="truncate text-[10.5px]">{p.lengan}</Sel><Sel className="text-center">{p.variasi_no || "-"}</Sel>
                  </tr>
                ))}</tbody>
              </table>
            </>
          )}
        </div>
      </div>

      {/* the cashier's note and the station sign-off across the page */}
      <div className="mt-[14px]">
        {d.catatan && (
          <div className="mb-[14px]">
            <Judul>Catatan Kasir/CS</Judul>
            <p data-testid="lk-catatan" className={`${GARIS} border-t-0 m-0 whitespace-pre-wrap px-1.5 py-1 text-[12.5px]`}>{d.catatan}</p>
          </div>
        )}
        <Judul>PARAF POS</Judul>
        <table data-testid="lk-paraf" className="w-full table-fixed border-collapse">
          <tbody>
            <tr className="text-center text-[10.5px] font-bold text-[#1020D0]">{["Desain", "Pattern", "Sublim", "Press", "Jahit", "QC", "Packing", "Serah"].map((x) => <Sel key={x}>{x}</Sel>)}</tr>
            <tr>{Array.from({ length: 8 }, (_, i) => <Sel key={i} className="h-[38px]" />)}</tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function LembarKerjaJersey({ spkId }) {
  const cfg = usePengaturan();
  const [d, setD] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [penuh, setPenuh] = React.useState(false);
  const [lebar, setLebar] = React.useState(LEBAR_A4);
  const [tinggi, setTinggi] = React.useState(0);
  const wadah = React.useRef(null);
  const lembar = React.useRef(null);
  React.useEffect(() => {
    setD(null); setError(null);
    apiLembarKerja(spkId).then(setD).catch((e) => setError(errText(e)));
  }, [spkId]);
  // fit the A4 sheet to the screen width (phones), measured again when the screen changes
  React.useEffect(() => {
    if (!wadah.current || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(() => {
      setLebar(wadah.current?.clientWidth || LEBAR_A4);
      setTinggi(lembar.current?.offsetHeight || 0);
    });
    ro.observe(wadah.current);
    if (lembar.current) ro.observe(lembar.current);
    return () => ro.disconnect();
  }, [d]);

  if (error) return <div data-testid="lk-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>;
  if (!d) return <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 size={16} className="animate-spin" /> Memuat lembar kerja…</div>;

  const skala = penuh ? 1 : skalaA4(lebar);
  return (
    <div data-testid="lembar-kerja" className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs text-slate-500">Lembar kerja A4 — sama seperti file Excel. Tanpa harga.{d.pemain_disembunyikan ? " Nama & nomor pemain tidak ditampilkan untuk pos ini." : ""}</p>
        <div className="flex items-center gap-2">
          {skalaA4(lebar) < 1 && (
            <button data-testid="lk-perbesar" type="button" onClick={() => setPenuh(!penuh)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-700 px-3 py-1.5 text-xs text-slate-300">
              {penuh ? <><ZoomOut size={14} /> Pas layar</> : <><ZoomIn size={14} /> Perbesar</>}
            </button>
          )}
          <TombolUnduhLembarKerja spkId={spkId} terang />
        </div>
      </div>
      <div ref={wadah} data-testid="lk-wadah" className={`w-full rounded-lg bg-slate-200/70 py-3 ${penuh ? "overflow-x-auto" : "overflow-hidden"}`}>
        <div style={skala < 1 ? { height: tinggi * skala + 2 } : undefined}>
          <div ref={lembar} className="mx-auto origin-top-left" style={skala < 1 ? { transform: `scale(${skala})`, width: LEBAR_A4 } : { width: LEBAR_A4 }}>
            <Lembar d={d} spkId={spkId} namaUsaha={cfg?.nama_usaha} />
          </div>
        </div>
      </div>
    </div>
  );
}
